import moment from 'moment';

import DatePicker from '../../../common/DatePicker';
import { RadioGroupOption } from '../../../common/RadioGroup/RadioGroup';
import { RadioGroupOptionPassdownProps } from '../../../common/RadioGroup/RadioGroup';
import { PauseEnrolmentContextType } from '../../pause-enrolment/PauseEnrolmentContext';
import ValidationErrors from '../../pause-enrolment/form-steps/PauseEnrolmentForm/ValidationErrors';
import {
  enrolmentIsPaused,
  enrolmentWillBePaused,
  formatStartOfTermText,
  getSpecificResumptionDateLabel,
  getStartOfTermLabel,
  weeksBeforeStartOfTermText,
} from '../../pause-enrolment/form-steps/PauseEnrolmentForm/helpers';
import { PauseFormValidationSchemaType } from '../../pause-enrolment/form-steps/PauseEnrolmentForm/validation';
import { isSummerCancelPromoEnabled } from '../../pause-enrolment/helpers';

export const createResumeOnStartOfTermOption = (context: PauseEnrolmentContextType): RadioGroupOption => {
  const { nextSchoolTerm, sessionPolicy, validationErrors } = context;
  const errors =
    validationErrors?.resumptionDate?.['resume-on-start-of-term']?._errors ||
    validationErrors?.resumptionDate?._errors ||
    null;
  const isFlexi = sessionPolicy?.pause?.pauseAllowed === 'Anytime';

  return {
    value: 'resume-on-start-of-term',
    labelForDropdown: formatStartOfTermText(nextSchoolTerm),
    renderOption: () => <div>{getStartOfTermLabel(nextSchoolTerm, isFlexi)}</div>,
    renderBelowOption: (props: RadioGroupOptionPassdownProps) => {
      const { checked, id } = props;

      return (
        <div>
          <div className="mt-0 flex flex-row">
            <div className="w-[3rem]"></div>
            <label htmlFor={id} className="cursor-pointer text-xs text-custom-4">
              {formatStartOfTermText(nextSchoolTerm)}
            </label>
          </div>
          <div className="ml-[3rem]">
            <ValidationErrors<PauseFormValidationSchemaType>
              fieldName="resumptionDate"
              checked={checked}
              errors={errors}
            />
          </div>
        </div>
      );
    },
  };
};

export const createResumeOnRevisionPeriodOption = (
  context: PauseEnrolmentContextType,
  label: string
): RadioGroupOption => {
  const { nextSchoolTerm, validationErrors } = context;
  const errors =
    validationErrors?.resumptionDate?.['resume-on-revision-period']?._errors ||
    validationErrors?.resumptionDate?._errors ||
    null;

  return {
    value: 'resume-on-revision-period',
    labelForDropdown: weeksBeforeStartOfTermText(nextSchoolTerm, 2),
    renderOption: () => <div>{label}</div>,
    renderBelowOption: (props: RadioGroupOptionPassdownProps) => {
      const { checked, id } = props;

      return (
        <div>
          <div className="mt-0 flex flex-row">
            <div className="w-[3rem]"></div>
            <label htmlFor={id} className="cursor-pointer text-xs text-custom-4">
              {weeksBeforeStartOfTermText(nextSchoolTerm, 2)}
            </label>
          </div>
          <div className="ml-[3rem]">
            <ValidationErrors<PauseFormValidationSchemaType>
              fieldName="resumptionDate"
              checked={checked}
              errors={errors}
            />
          </div>
        </div>
      );
    },
  };
};

export const createResumeAWeekBeforeTermStartOption = (
  context: PauseEnrolmentContextType,
  label: string
): RadioGroupOption => {
  const { nextSchoolTerm, validationErrors } = context;
  const errors =
    validationErrors?.resumptionDate?.['resume-a-week-before-term-start']?._errors ||
    validationErrors?.resumptionDate?._errors ||
    null;

  return {
    value: 'resume-a-week-before-term-start',
    labelForDropdown: weeksBeforeStartOfTermText(nextSchoolTerm, 1),
    renderOption: () => <div>{label}</div>,
    renderBelowOption: (props: RadioGroupOptionPassdownProps) => {
      const { checked, id } = props;

      return (
        <div>
          <div className="mt-0 flex flex-row">
            <div className="w-[3rem]"></div>
            <label htmlFor={id} className="cursor-pointer text-xs text-custom-4">
              {weeksBeforeStartOfTermText(nextSchoolTerm, 1)}
            </label>
          </div>
          <div className="ml-[3rem]">
            <ValidationErrors<PauseFormValidationSchemaType>
              fieldName="resumptionDate"
              checked={checked}
              errors={errors}
            />
          </div>
        </div>
      );
    },
  };
};

export const createResumeOnSpecificDateOption = (context: PauseEnrolmentContextType): RadioGroupOption => {
  const {
    specificResumptionDate,
    setSpecificResumptionDate,
    specificResumptionMinDate,
    specificResumptionMaxDate,
    nextSchoolTerm,
    validationErrors,
  } = context;
  const resumptionDateValidationErrors =
    validationErrors?.resumptionDate?.['resume-on-specific-date']?._errors ||
    validationErrors?.resumptionDate?._errors ||
    null;

  return {
    value: 'resume-on-specific-date',
    labelForDropdown: 'Specific date',
    renderOption: () => <div>{getSpecificResumptionDateLabel(nextSchoolTerm)}</div>,
    renderBelowOption: (props: RadioGroupOptionPassdownProps) => {
      const { checked, id } = props;

      return (
        <div className="ml-[3rem] max-w-[320px]">
          <DatePicker
            key={`${id}_checked_${checked}`} // need this dynamic key to force the native input to re-render
            placeholder="Select resumption date"
            id={id}
            disabled={!checked}
            options={{
              mode: 'single',
              minDate: specificResumptionMinDate.utc().format(),
              maxDate: specificResumptionMaxDate.utc().format(),
            }}
            value={specificResumptionDate}
            onChange={(date: Date[]) => {
              setSpecificResumptionDate(date[0].toISOString());
            }}
          />
          <ValidationErrors<PauseFormValidationSchemaType>
            fieldName="resumptionDate"
            checked={checked}
            errors={resumptionDateValidationErrors}
          />
        </div>
      );
    },
    renderBelowDropdownOption: (props: RadioGroupOptionPassdownProps) => {
      const { checked, id } = props;

      return (
        <div className="mt-4 max-w-[320px]">
          <DatePicker
            key={`${id}_checked_${checked}`} // need this dynamic key to force the native input to re-render
            placeholder="Select resumption date"
            id={id}
            disabled={!checked}
            options={{
              mode: 'single',
              minDate: specificResumptionMinDate.utc().format(),
              maxDate: specificResumptionMaxDate.utc().format(),
            }}
            value={specificResumptionDate}
            onChange={(date: Date[]) => {
              setSpecificResumptionDate(date[0].toISOString());
            }}
          />
          <ValidationErrors<PauseFormValidationSchemaType>
            fieldName="resumptionDate"
            checked={checked}
            errors={resumptionDateValidationErrors}
          />
        </div>
      );
    },
  };
};

export const createPauseResumptionOptionsForCancelPromo = (context: PauseEnrolmentContextType): RadioGroupOption[] => {
  const { sessionPolicy, currentOrUpcomingHoliday, nextSchoolTerm, pauseMinDate, pauseMaxDate, selectedEnrolment } =
    context;
  const isFlexi = sessionPolicy?.pause?.pauseAllowed === 'Anytime';
  const isPausingForSummerHoliday = currentOrUpcomingHoliday?.type === 'Summer Holiday';
  const isWithinPauseWindow = moment().isBetween(pauseMinDate, pauseMaxDate, 'day', '[]');
  const isPaused = enrolmentIsPaused(selectedEnrolment);
  const willBePaused = enrolmentWillBePaused(selectedEnrolment);
  const summerCancelPromoEnabled = isSummerCancelPromoEnabled();

  if (
    isFlexi &&
    isPausingForSummerHoliday &&
    (isWithinPauseWindow || isPaused || willBePaused || summerCancelPromoEnabled)
  ) {
    return [
      createResumeOnRevisionPeriodOption(context, 'Get ahead of the year'),
      createResumeAWeekBeforeTermStartOption(context, `A week before ${nextSchoolTerm?.schoolTerm} starts`),
      createResumeOnStartOfTermOption(context),
      createResumeOnSpecificDateOption(context),
    ];
  }

  if (isFlexi) {
    return []; // Flexi will pause via "Skip by X weeks" method
  }

  if (isPausingForSummerHoliday && (isWithinPauseWindow || summerCancelPromoEnabled)) {
    return [
      createResumeOnRevisionPeriodOption(context, 'Get ahead of the year'),
      createResumeAWeekBeforeTermStartOption(context, `A week before ${nextSchoolTerm?.schoolTerm} starts`),
      createResumeOnStartOfTermOption(context),
    ];
  }
  if (isWithinPauseWindow) {
    return [createResumeOnStartOfTermOption(context)];
  }

  return [];
};
